import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ShieldCheck, ShieldAlert, ChevronRight, Loader2, Upload, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Severity colors
const SEVERITY_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#2563eb',
  info: '#6b7280',
};

function SecurityScanningWidget() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const storedSession = localStorage.getItem('eyvo_session');
        const response = await fetch(`${API_URL}/api/pentest/summary`, {
          credentials: 'include',
          headers: storedSession ? { 'Authorization': `Bearer ${storedSession}` } : {},
        });
        if (response.ok) {
          const data = await response.json();
          setSummary(data);
        }
      } catch (error) {
        console.error('Failed to fetch pentest summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="eyvo-card security-scanning-widget loading">
        <Loader2 className="spin" size={20} />
      </div>
    );
  }

  if (!summary || summary.total_reports === 0) {
    return (
      <div className="eyvo-card security-scanning-widget empty" data-testid="security-scanning-widget">
        <div className="empty-state-mini">
          <div className="empty-icon">
            <Upload size={32} color="#6b7280" />
          </div>
          <div className="empty-text">
            <h4>No Security Scans Uploaded</h4>
            <p>Upload penetration test reports to track vulnerabilities and map findings to SOC 2 controls.</p>
          </div>
          <Link to="/pentest" className="btn-primary" data-testid="upload-pentest-btn">
            <Upload size={16} /> Upload Report
          </Link>
        </div>
      </div>
    );
  }

  const { total_reports, by_report_type, aggregate_severity, malware_status, blacklist_status } = summary;

  // Check if all blacklist services are safe
  const allBlacklistSafe = Object.values(blacklist_status || {}).every(s => s === 'SAFE');
  
  // Calculate total findings
  const totalFindings = Object.values(aggregate_severity || {}).reduce((a, b) => a + b, 0);
  const criticalHigh = (aggregate_severity?.critical || 0) + (aggregate_severity?.high || 0);

  return (
    <div className="eyvo-card security-scanning-widget" data-testid="security-scanning-widget">
      {/* Stats Row */}
      <div className="scanning-stats">
        <div className="stat-item">
          <FileText size={20} color="#2563eb" />
          <div className="stat-content">
            <span className="stat-value">{total_reports}</span>
            <span className="stat-label">Reports</span>
          </div>
        </div>
        <div className="stat-item">
          <Shield size={20} color="#d97706" />
          <div className="stat-content">
            <span className="stat-value">{totalFindings}</span>
            <span className="stat-label">Findings</span>
          </div>
        </div>
        <div className="stat-item">
          <ShieldAlert size={20} color="#dc2626" />
          <div className="stat-content">
            <span className="stat-value">{criticalHigh}</span>
            <span className="stat-label">Critical/High</span>
          </div>
        </div>
        <div className="stat-item">
          {malware_status === 'clean' ? (
            <ShieldCheck size={20} color="#059669" />
          ) : (
            <ShieldAlert size={20} color="#d97706" />
          )}
          <div className="stat-content">
            <span className="stat-value" style={{ color: malware_status === 'clean' ? '#059669' : '#d97706' }}>
              {malware_status === 'clean' ? 'Clean' : 'Check'}
            </span>
            <span className="stat-label">Malware</span>
          </div>
        </div>
      </div>

      {/* Severity Breakdown */}
      <div className="severity-breakdown">
        <span className="breakdown-label">By Severity:</span>
        <div className="severity-pills">
          {['critical', 'high', 'medium', 'low', 'info'].map((sev) => (
            <span
              key={sev}
              className="severity-pill"
              style={{ 
                color: SEVERITY_COLORS[sev],
                background: SEVERITY_COLORS[sev] + '15',
                padding: '4px 8px',
                borderRadius: '6px',
              }}
            >
              {aggregate_severity?.[sev] || 0} {sev.charAt(0).toUpperCase() + sev.slice(1)}
            </span>
          ))}
        </div>
      </div>

      {/* Scan Types */}
      <div className="scan-types-grid">
        {['web_application_audit', 'vulnerability_assessment', 'malware_monitoring'].map((type) => {
          const info = by_report_type?.[type];
          const labels = {
            web_application_audit: 'Web App Audit',
            vulnerability_assessment: 'VA Scan',
            malware_monitoring: 'Malware Check',
          };
          return (
            <div key={type} className="scan-type-item">
              <span className="scan-label">{labels[type]}</span>
              {info ? (
                <span className="scan-status ok">
                  <ShieldCheck size={14} /> {info.count} report{info.count !== 1 ? 's' : ''}
                </span>
              ) : (
                <span className="scan-status missing">Not uploaded</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Link to full page */}
      <Link to="/pentest" className="widget-link">
        View All Security Scans <ChevronRight size={14} />
      </Link>
    </div>
  );
}

export default SecurityScanningWidget;
